import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Badge, Button } from '@gradebook/ui';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { holdsAnyRole } from '@/lib/roles';
import { useCourseScopeStore } from '@/store/courseScopeStore';
import { useCourseScope } from '@/hooks/useCourseScope';
import { useIdentifierText } from '@/hooks/useIdentifierText';
import { termStatus } from '@/lib/academicYearTerms';

/**
 * The class in scope, as two pills on the shell's topbar.
 *
 * This is where the gradebook's 256px course rail went. Which class you are
 * looking at is session context — the same kind of fact as the academic year,
 * and asked once rather than by every page that needs it — so it belongs beside
 * the year, and the page gets its width back.
 *
 * The pills are page context in shared chrome, so they render only while a page
 * that is course-scoped is on screen (`useCourseScopePills`). A Course pill
 * above the student roster would be naming a scope that page does not have.
 */

/** Both pills answer to the same two gates, and neither may call a hook first. */
function useScopeVisible(): boolean {
  const effectiveRoles = useAuthStore((state) => state.getEffectiveRoles());
  const scopeCount = useCourseScopeStore((state) => state.scopeCount);

  // `/api/courses` and `/api/terms` are teacher-or-admin only, so for a student
  // or parent these could only ever render a 403. The check is above the
  // querying component, so those requests are never made.
  // The whole set, not the primary office - see `AppSidebar`.
  const isStaff = holdsAnyRole(effectiveRoles, ['admin', 'teacher']);
  return isStaff && scopeCount > 0;
}

export function CourseScopePill() {
  return useScopeVisible() ? <CoursePill /> : null;
}

export function TermScopePill() {
  return useScopeVisible() ? <TermPill /> : null;
}

function CoursePill() {
  const { course, courseGroups, selectCourse, term, terms, selectTerm } =
    useCourseScope();
  // The code is decoration on the course name here, so a school that does not
  // code its catalogue simply loses it - name and level are the whole label.
  const identifiers = useIdentifierText();

  return (
    <Popover className="relative min-w-0">
      <PopoverButton
        aria-label="Course"
        className={cn(
          'flex min-w-0 max-w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-full border border-primary-200 bg-card px-2.5 py-[7px] text-primary-900 shadow-2xs sm:gap-2 sm:px-3',
          'transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        {/* The eyebrow and the course code are the pill's widest, least
            load-bearing parts. On a phone the topbar is fighting for ~390px, so
            they drop and the course name — the thing the control is for —
            stays. `aria-label` still says what this is. */}
        <span className="hidden text-[10px] font-semibold uppercase leading-none tracking-[0.06em] text-primary-400 sm:inline">
          Course
        </span>
        <span className="truncate text-[13px] font-bold leading-none">
          {course?.courseName ?? 'No course'}
        </span>
        {identifiers.shows.courseCode && course?.courseCode && (
          <span className="hidden whitespace-nowrap text-[11px] leading-none text-primary-400 lg:inline">
            {course.courseCode}
          </span>
        )}
        <ChevronDownIcon className="size-3.5 flex-none text-primary-600" />
      </PopoverButton>

      <PopoverPanel
        role="dialog"
        aria-label="Switch course"
        // Unlike the year and term pills this one hangs off its *left* edge,
        // and that edge is a long way in — the brand sits to its left. A width
        // measured from the viewport would therefore run off the right of a
        // phone and widen the whole document, so below `sm` the panel is pinned
        // to the viewport instead and spans it. From `sm` up there is room to
        // drop straight out of the pill.
        className="fixed left-3 right-3 top-11 z-30 w-auto overflow-hidden rounded-[18px] border bg-card shadow-[0_16px_40px_rgba(0,0,0,0.16)] sm:absolute sm:left-0 sm:right-auto sm:w-[min(346px,calc(100vw-72px))]"
      >
        {({ close }) => (
          <>
            <div className="border-b border-neutral-100 px-3.5 py-[11px]">
              <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.07em] text-muted-foreground">
                Switch course
              </span>
            </div>

            <div className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
              {/* The topbar cannot hold both pills on a phone — everything else
                  has already collapsed to nothing by 390px — so the Term pill
                  drops there and its job moves in here. Switching term stays
                  possible at every width, which the rail this replaced could
                  also do. */}
              {terms.length > 1 && (
                <div className="sm:hidden" role="group" aria-label="Term">
                  <div className="px-[11px] pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                    Term
                  </div>
                  {terms.map((item) => {
                    const isSelected = item.id === term?.id;
                    const itemStatus = termStatus(item);
                    return (
                      <Button
                        key={item.id}
                        type="button"
                        variant="ghost"
                        aria-current={isSelected ? 'true' : undefined}
                        onClick={() => {
                          selectTerm(item.id);
                          close();
                        }}
                        className={cn(
                          'flex h-auto w-full items-center justify-start gap-[7px] rounded-[14px] px-[11px] py-[9px] text-left font-normal',
                          isSelected ? 'bg-primary-50' : 'hover:bg-muted'
                        )}
                      >
                        <span
                          className={cn(
                            'truncate text-[12.5px]',
                            isSelected
                              ? 'font-semibold text-primary-900'
                              : 'font-medium text-foreground'
                          )}
                        >
                          {item.termName}
                        </span>
                        <span className="ml-auto flex-none">
                          <Badge variant={itemStatus.variant} size="sm">
                            {itemStatus.label}
                          </Badge>
                        </span>
                      </Button>
                    );
                  })}
                </div>
              )}
              {courseGroups.length === 0 ? (
                <p className="px-[11px] py-3 text-[12.5px] text-muted-foreground">
                  No courses run in this term.
                </p>
              ) : (
                courseGroups.map((group) => (
                  <div key={group.key} role="group" aria-label={group.label}>
                    <div className="px-[11px] pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                      {group.label}
                    </div>
                    {group.items.map((item) => {
                      const isSelected = item.id === course?.id;
                      return (
                        <Button
                          key={item.id}
                          type="button"
                          variant="ghost"
                          aria-current={isSelected ? 'true' : undefined}
                          onClick={() => {
                            selectCourse(item.id);
                            close();
                          }}
                          className={cn(
                            'flex h-auto w-full items-center justify-start gap-[7px] rounded-[14px] px-[11px] py-[9px] text-left font-normal',
                            isSelected ? 'bg-primary-50' : 'hover:bg-muted'
                          )}
                        >
                          <span
                            className={cn(
                              'truncate text-[12.5px]',
                              isSelected
                                ? 'font-semibold text-primary-900'
                                : 'font-medium text-foreground'
                            )}
                          >
                            {item.courseName}
                          </span>
                          <span className="ml-auto flex-none text-[10.5px] text-muted-foreground">
                            {/* The separator goes with the code: an uncoded
                                course would otherwise read " · HL". */}
                            {[identifiers.shows.courseCode ? item.courseCode : null, item.level]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}

function TermPill() {
  const { term, terms, selectTerm } = useCourseScope();

  if (!term) return null;

  const status = termStatus(term);

  return (
    // Hidden on a phone, where the topbar has room for one scope pill and the
    // course is the one the page is about. The term moves into the course menu
    // at that width rather than becoming unreachable.
    <Popover className="relative hidden flex-none sm:block">
      <PopoverButton
        aria-label="Term"
        className={cn(
          'flex flex-none cursor-pointer items-center gap-1.5 rounded-full border border-neutral-200 bg-card px-2.5 py-[7px] sm:gap-2 sm:px-3',
          'transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        <span className="hidden text-[10px] font-semibold uppercase leading-none tracking-[0.06em] text-neutral-400 sm:inline">
          Term
        </span>
        <span className="whitespace-nowrap text-[13px] font-bold leading-none text-neutral-800">
          {term.termName}
        </span>
        <span className="hidden sm:inline">
          <Badge variant={status.variant} size="sm">
            {status.label}
          </Badge>
        </span>
        <ChevronDownIcon className="size-3.5 flex-none text-neutral-500" />
      </PopoverButton>

      <PopoverPanel
        role="dialog"
        aria-label="Switch term"
        className="absolute right-0 top-11 z-30 w-[min(300px,calc(100vw-72px))] overflow-hidden rounded-[18px] border bg-card shadow-[0_16px_40px_rgba(0,0,0,0.16)]"
      >
        {({ close }) => (
          <>
            <div className="border-b border-neutral-100 px-3.5 py-[11px]">
              <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.07em] text-muted-foreground">
                Switch term
              </span>
            </div>
            <div className="p-1.5">
              {terms.map((item) => {
                const itemStatus = termStatus(item);
                const isSelected = item.id === term.id;
                return (
                  <Button
                    key={item.id}
                    type="button"
                    variant="ghost"
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={() => {
                      selectTerm(item.id);
                      close();
                    }}
                    className={cn(
                      'mt-0.5 flex h-auto w-full items-center justify-start gap-[7px] rounded-[14px] px-[11px] py-[9px] text-left font-normal',
                      isSelected ? 'bg-primary-50' : 'hover:bg-muted'
                    )}
                  >
                    <span
                      className={cn(
                        'text-[12.5px]',
                        isSelected
                          ? 'font-semibold text-primary-900'
                          : 'font-medium text-foreground'
                      )}
                    >
                      {item.termName}
                    </span>
                    <span className="ml-auto">
                      <Badge variant={itemStatus.variant} size="sm">
                        {itemStatus.label}
                      </Badge>
                    </span>
                  </Button>
                );
              })}
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
